import React, { useState, useEffect } from "react";
import {
  Dialog,
  Heading,
  TextInputField,
  FormField,
  Button,
  Pane,
  IconButton,
  SelectField,
  CrossIcon,
  Switch,
} from "evergreen-ui";
import { Container, Row, Col, setConfiguration } from "react-grid-system";

import { mutate } from "swr";
import { useForm } from "react-hook-form";

import { useQueryBanks } from "../../services/api/banksService";
import {
  getEntry,
  putEntry,
  useQueryEntries,
  useQueryEntry,
} from "../../services/api/entriesService";
import {
  getIntentColorByJournal,
  getNameByJournal,
} from "../../helpers/entriesHelper";
import styles from "./EntryFormComponent.module.css";

import { toastNotification } from "../../shared/toastNotification";
import { inputTransferValidation } from "../../helpers/formHelper";
import { useSelector } from "react-redux";
import { useQueryVitaUserByIdentityId } from "../../services/api/vitaUserService";
import { compareAsc } from "date-fns";

function TransferEditFormComponent({
  isShown,
  setIsShown,
  setEntryJournalOnFocus,
}) {
  const companyId = localStorage.getItem("companyId");
  const userId = localStorage.getItem("userId");
  const [isDisabled, setIsDisabled] = useState(true);
  const [minDate, setMinDate] = useState(null);

  /* API calls */

  const { data: banks = [], isLoading: isLoadingBanks } = useQueryBanks(
    companyId,
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const {
    data: vitaUser,
    isLoading: isLoadingVitaUser,
    isError,
  } = useQueryVitaUserByIdentityId(userId, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  /* Form */

  const journalEntry = useSelector((state) => state.entryValues.transferEdit);

  const {
    formState,
    register,
    handleSubmit,
    reset,
    getValues,
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      ...journalEntry,
      statusPayment: journalEntry?.statusPayment || false,
      value:
        journalEntry?.value?.toLocaleString("pt-BR", {
          minimumFractionDigits: 2,
        }) || "0,00",
    },
  });

  setConfiguration({
    containerWidths: "100%",
    gutterWidth: "12",
  });

  const checkShouldDisable = (entry, bankData) => {
    if (!entry || !bankData) return true;

    const sourceBank = bankData.find((bank) => bank.id === entry.bankAccountId);
    const destBank = bankData.find(
      (bank) => bank.id === entry.bankAccountIdDestination
    );

    if (!sourceBank || !destBank) return true;

    const entryDate = new Date(entry.dateCompetencia);
    let shouldDisable = false;

    if (sourceBank.reconciliationDate) {
      const sourceRecDate = new Date(sourceBank.reconciliationDate);
      if (compareAsc(entryDate, sourceRecDate) <= 0) {
        shouldDisable = true;
      }
    }

    if (destBank.reconciliationDate && !shouldDisable) {
      const destRecDate = new Date(destBank.reconciliationDate);
      if (compareAsc(entryDate, destRecDate) <= 0) {
        shouldDisable = true;
      }
    }

    if (
      entry.fitId !== null ||
      (entry.journalEntryFromRef &&
        entry?.journalEntryFromRef?.fitId !== null) ||
      (entry.journalEntryRef && entry?.journalEntryRef?.fitId !== null)
    ) {
      shouldDisable = true;
    }

    return shouldDisable;
  };

  const getTransferEntry = (journalEntry) => {
    if (!journalEntry || !banks?.data) return;

    console.log("getTransferEntryJournalEntry: ", journalEntry);
    const tempEntry = { ...journalEntry };
    tempEntry.dateCompetencia = tempEntry.dateCompetencia.substr(0, 10);
    tempEntry.dateVencimento = tempEntry.dateVencimento.substr(0, 10);

    if (!tempEntry.isInput) {
      tempEntry.bankAccountIdDestination =
        tempEntry.journalEntryFromRef.bankAccountId;
    } else {
      console.log(tempEntry);
      tempEntry.id = tempEntry.journalEntryRef.id;
      const destinationBank = tempEntry.bankAccountId;
      const sourceBank = tempEntry.journalEntryRef.bankAccountId;
      tempEntry.bankAccountIdDestination = destinationBank;
      tempEntry.bankAccountId = sourceBank;
    }

    reset(tempEntry);
    setValue(
      "value",
      tempEntry.value.toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
      }),
      { shouldDirty: false } // Explicitly mark as not dirty when initializing
    );

    // Check disable state when loading entry
    const shouldDisable = checkShouldDisable(tempEntry, banks.data);
    setIsDisabled(shouldDisable);

    console.log("getTransferEntryUpdated: ", tempEntry);
  };

  useEffect(() => {
    // Reset disable state when dialog opens/closes
    if (!isShown) {
      setIsDisabled(true);
      reset();
      return;
    }

    if (!journalEntry || !banks?.data) return;

    // Force immediate check when a new entry is loaded
    const formData = getValues();
    const shouldDisable = checkShouldDisable(formData, banks.data);
    setIsDisabled(shouldDisable);

    if (shouldDisable) {
      // When disabled, ensure values are preserved but not editable
      setValue("statusPayment", journalEntry.statusPayment);
      setValue(
        "value",
        journalEntry.value.toLocaleString("pt-BR", {
          minimumFractionDigits: 2,
        })
      );
      setValue("dateCompetencia", journalEntry.dateCompetencia.substr(0, 10));

      if (journalEntry.isInput) {
        setValue("bankAccountId", journalEntry.journalEntryRef.bankAccountId);
        setValue("bankAccountIdDestination", journalEntry.bankAccountId);
      } else {
        setValue("bankAccountId", journalEntry.bankAccountId);
        setValue(
          "bankAccountIdDestination",
          journalEntry.journalEntryFromRef.bankAccountId
        );
      }
    }
  }, [
    isShown,
    banks,
    journalEntry, // Watch the entire journalEntry object
  ]);

  useEffect(() => {
    if (isShown && journalEntry && banks?.data) {
      getTransferEntry(journalEntry);
    }
  }, [isShown, journalEntry, banks]);

  // This ensures values are preserved when loading asynchronously
  useEffect(() => {
    if (isDisabled && journalEntry && banks?.data) {
      // For input transfers, handle bank accounts appropriately
      if (journalEntry.isInput) {
        setValue("bankAccountId", journalEntry.journalEntryRef?.bankAccountId);
        setValue("bankAccountIdDestination", journalEntry.bankAccountId);
      } else {
        setValue("bankAccountId", journalEntry.bankAccountId);
        setValue(
          "bankAccountIdDestination",
          journalEntry.journalEntryFromRef?.bankAccountId
        );
      }
    }
  }, [isDisabled, banks, journalEntry]);

  const handleInput = (event) => {
    let inputVal = event.target.value.replace(/\D/g, "");
    const initialValue = journalEntry?.value || 0;

    if (inputVal === "") {
      inputVal = "0";
    }

    // Convert both values to same format for comparison (cents)
    const newValue = Number(inputVal);
    const initialValueInCents = Math.round(initialValue * 100);

    // Format for display
    inputVal = inputVal.padStart(3, "0");
    const wholeNum = inputVal.slice(0, -2);
    const decimal = inputVal.slice(-2);
    const formattedWholeNum = Number(wholeNum).toLocaleString();
    const formattedValue = `${formattedWholeNum},${decimal}`;

    // Compare values in cents to avoid floating point issues
    setValue("value", formattedValue, {
      shouldDirty: newValue !== initialValueInCents,
    });
  };

  // Function to get min date based on bank reconciliation dates
  const calculateMinDate = (sourceBankId, destBankId) => {
    if (!banks?.data) return null;

    const sourceBank = banks.data.find((bank) => bank.id === sourceBankId);
    const destBank = banks.data.find((bank) => bank.id === destBankId);

    let latestRecDate = null;

    if (sourceBank?.reconciliationDate) {
      const sourceRecDate = new Date(sourceBank.reconciliationDate);
      latestRecDate = sourceRecDate;
    }

    if (destBank?.reconciliationDate) {
      const destRecDate = new Date(destBank.reconciliationDate);
      if (!latestRecDate || destRecDate > latestRecDate) {
        latestRecDate = destRecDate;
      }
    }

    if (latestRecDate) {
      // Add one day to the latest reconciliation date
      latestRecDate.setDate(latestRecDate.getDate() + 1);
      return latestRecDate.toISOString().split("T")[0];
    }

    return null;
  };

  // Watch bank changes to update min date
  useEffect(() => {
    const sourceBankId = watch("bankAccountId");
    const destBankId = watch("bankAccountIdDestination");

    if (sourceBankId && destBankId) {
      const newMinDate = calculateMinDate(sourceBankId, destBankId);
      setMinDate(newMinDate);

      // If current date is before min date, update to min date
      const currentDate = watch("dateCompetencia");
      if (currentDate && newMinDate && currentDate < newMinDate) {
        setValue("dateCompetencia", newMinDate);
      }
    }
  }, [watch("bankAccountId"), watch("bankAccountIdDestination"), banks]);

  const buildFormHeader = () => {
    return (
      <Pane
        display="flex"
        flex={1}
        justifyContent="space-between"
        alignItems="center"
      >
        <Button
          appearance="primary"
          intent={getIntentColorByJournal(journalEntry)}
          height={48}
          marginRight={16}
        >
          {getNameByJournal(journalEntry)}
        </Button>
        <Button
          appearance="minimal"
          height={28}
          marginRight={56}
          style={{
            background: "#BFFCC0",
            color: "black",
          }}
          cursor="normal"
        >
          Modo Edição
        </Button>
        <IconButton appearance="minimal" icon={CrossIcon} onClick={onClose} />
      </Pane>
    );
  };

  const buildFormFooter = () => {
    const currentDescription = watch("description");
    const anyFieldChanged = formState.isDirty;
    console.log("formState: ", formState.isDirty);

    return (
      <Pane>
        <Button
          form="transfer-form"
          type="submit"
          width="fit-content"
          // In disabled mode, enable only when description changes
          // In enabled mode, enable whenever any field changes
          disabled={
            isDisabled
              ? currentDescription === journalEntry?.description
              : !anyFieldChanged
          }
        >
          Salvar
        </Button>
      </Pane>
    );
  };

  const onSubmit = (entry) => {
    if (!journalEntry) return;

    let statusResponse = false;
    let toastContent = "";

    // Start with all current form values
    const currentFormValues = getValues();

    console.log("currentFormValues: ", currentFormValues);

    let entryToUpdate = {
      ...currentFormValues,
      companyId,
      journalEntryType: 5,
      userUpdateId: !isLoadingVitaUser && vitaUser.data.id,
      // Always update description regardless of disabled state
      dateCompetencia: journalEntry.dateCompetencia,
      dateVencimento: journalEntry.dateVencimento,
      description: currentFormValues.description,
      value: Number(entry.value.replace(/\./g, "").replace(",", ".")),
      bankAccountId: currentFormValues.bankAccountId,
      bankAccountIdDestination: currentFormValues.bankAccountIdDestination,
    };

    console.log("entryToUpdate: ", entryToUpdate);

    // If not disabled, update all editable fields
    if (!isDisabled) {
      entryToUpdate = {
        ...entryToUpdate,
        dateCompetencia: currentFormValues.dateCompetencia,
        dateVencimento: currentFormValues.dateCompetencia,
        statusPayment: currentFormValues.statusPayment,
        value: Number(entry.value.replace(/\./g, "").replace(",", ".")),
        bankAccountId: journalEntry.isInput
          ? currentFormValues.bankAccountIdDestination
          : currentFormValues.bankAccountId,
        bankAccountIdDestination: journalEntry.isInput
          ? currentFormValues.bankAccountId
          : currentFormValues.bankAccountIdDestination,
      };
    }

    putEntry(entryToUpdate)
      .then((res) => {
        console.log("res put entry: ", res);
        mutate("entriesFilteredByDate");
        mutate("entriesReconciliation");
        mutate("bankBalances");
        statusResponse = "success";
        toastContent = "Lançamento salvo com sucesso.";
        onClose();
      })
      .catch((err) => {
        statusResponse = "warning";
        console.log("put err", err);
        toastContent = `Verifique os campos novamente.`;
      })
      .finally(() => {
        toastNotification(statusResponse, toastContent);
        mutate("banks");
      });
  };

  const onClose = () => {
    reset();
    setIsDisabled(true); // Reset disabled state when closing
    setIsShown(false);
  };

  const handleStatusPaymentChange = (e) => {
    // Only control the value if disabled
    if (isDisabled) {
      setValue("statusPayment", journalEntry.statusPayment);
    } else {
      setValue("statusPayment", e.target.checked);
    }
  };

  return (
    <Dialog
      isShown={isShown}
      title=""
      onCloseComplete={onClose}
      confirmLabel="Custom Label"
      hasFooter={true}
      header={() => buildFormHeader()}
      footer={buildFormFooter}
      shouldCloseOnEscapePress={!formState.isDirty}
      width={800}
      backgroundColor="#f8f8f8"
    >
      <div className={styles.dialogWrapper}>
        <form id="transfer-form" onSubmit={handleSubmit(onSubmit)}>
          <FormField className={styles.formArea}>
            <Container
              fluid={true}
              style={{
                width: "100%",
                margin: "none",
                padding: "none",
              }}
            >
              <Row>
                <Col sm={12} md={12} lg={6}>
                  <SelectField
                    {...register("bankAccountId")}
                    label="Conta de Saída"
                    defaultValue={undefined}
                    onChange={(e) => setValue("bankAccountId", e.target.value)}
                    disabled={isDisabled}
                  >
                    <option value={undefined}></option>
                    {!isLoadingBanks &&
                      banks.data.map((bank) => {
                        return (
                          <option key={bank.id} value={bank.id}>
                            {bank.name}
                          </option>
                        );
                      })}
                  </SelectField>
                </Col>
                <Col sm={12} md={12} lg={6}>
                  <SelectField
                    {...register("bankAccountIdDestination")}
                    label="Conta de Entrada"
                    defaultValue={undefined}
                    disabled={isDisabled}
                    onChange={(e) =>
                      setValue("bankAccountIdDestination", e.target.value)
                    }
                  >
                    <option value={undefined}></option>

                    {!isLoadingBanks &&
                      banks.data.map((bank) => {
                        return (
                          <option key={bank.id} value={bank.id}>
                            {bank.name}
                          </option>
                        );
                      })}
                  </SelectField>
                </Col>
              </Row>
              <Row>
                <Col sm={12} md={6} lg={6}>
                  <TextInputField
                    {...register("dateCompetencia")}
                    label="Data da Transferência"
                    id="dateCompetencia"
                    name="dateCompetencia"
                    type="date"
                    min={minDate}
                    onChange={(e) => {
                      setValue("dateCompetencia", e.target.value);
                    }}
                    disabled={isDisabled}
                  />
                </Col>
                <Col sm={12} md={6} lg={6}>
                  <TextInputField
                    {...register("description")}
                    label="Descrição"
                    id="description"
                    name="description"
                    placeholder=""
                    defaultValue=""
                  />
                </Col>
              </Row>
              <Row>
                <Col xs={10} sm={11} md={11} lg={12}>
                  <TextInputField
                    {...register("value")}
                    label="Valor (R$)"
                    type="text"
                    id="value"
                    name="value"
                    placeholder=""
                    defaultValue={
                      journalEntry?.value?.toLocaleString("pt-BR", {
                        minimumFractionDigits: 2,
                      }) || "0,00"
                    }
                    onChange={handleInput}
                    disabled={isDisabled}
                  />
                </Col>
              </Row>
              <Row>
                <Col xs={2} sm={1} md={1} lg={1}>
                  <Heading size={400} marginBottom={16}>
                    Pago
                  </Heading>
                  <Switch
                    {...register("statusPayment")}
                    checked={
                      isDisabled
                        ? journalEntry.statusPayment
                        : watch("statusPayment")
                    }
                    onChange={handleStatusPaymentChange}
                  />
                </Col>
              </Row>
            </Container>
          </FormField>
        </form>
      </div>
    </Dialog>
  );
}

export default TransferEditFormComponent;
